/** Home-page "From where you are" band — condensed local summary linking to /local. */
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { usePlace } from "@/lib/LocationContext";
import { useLocalBundle } from "@/lib/useLocalBundle";
import { weatherInfo } from "@/lib/weather";
import { NewsList, placeLine } from "@/pages/Local";

export default function LocalBand() {
  const { place, pinned, detecting } = usePlace();
  const { data } = useLocalBundle(pinned ? place : null);

  // Show only once the visitor has a confirmed place — the full page handles the rest.
  if (detecting || !place || !pinned) return null;

  const weather = data?.weather?.temperature != null ? data.weather : null;
  const news = data?.news || [];
  const info = weather ? weatherInfo(weather.weather_code, weather.is_day) : null;

  return (
    <section className="border-y border-[color:var(--sepia)]/20 bg-[color:var(--parchment-2)]/40" data-testid="home-local-band">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline text-[color:var(--ink-soft)] mb-3 flex items-center gap-2">
              <MapPin size={12} className="text-[color:var(--rust)]" /> From where you are
            </div>
            <Link to="/local" data-testid="home-local-link" className="group block">
              <div className="font-serif-display leading-tight group-hover:text-[color:var(--rust)] transition-colors duration-300" style={{ fontSize: "clamp(28px, 4vw, 52px)" }}>
                {placeLine(place)}
              </div>
            </Link>
          </div>
          {weather && info && (
            <div className="flex items-center gap-4 border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] px-5 py-3" data-testid="home-local-weather">
              <info.Icon size={26} className="text-[color:var(--sepia)]" />
              <div>
                <div className="font-serif-display text-3xl leading-none">{Math.round(weather.temperature)}°</div>
                <div className="numeric-index text-[color:var(--ink-soft)] mt-1">{weather.label || info.label}</div>
              </div>
            </div>
          )}
        </div>

        {news.length > 0 && (
          <div className="mt-10 grid lg:grid-cols-2 gap-x-12">
            <NewsList news={news.slice(0, 3)} testid="home-local-news" />
            <div className="hidden lg:flex items-start justify-end">
              <div className="max-w-sm border-l-2 border-[color:var(--rust)]/60 pl-5">
                <p className="font-italic-serif italic text-lg leading-[1.6] text-[color:var(--ink-soft)]">
                  "To know where you are going, first know the ground on which you stand."
                </p>
                <Link to="/local" data-testid="home-local-open" className="mt-4 inline-flex items-center gap-1 text-sm link-underline text-[color:var(--rust)]">
                  Weather, news and the story of {place.city} <ArrowUpRight size={13} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
